/** The canonical join between a variant's option values — mirrors the backend's `optionValues.joinToString(" / ")`. */
const OPTION_SEP = ' / ';
/**
 * THE IMAGE AXIS — the single option whose values the seller photographed ("Couleur"), discovered from the
 * photos themselves rather than declared anywhere. Returns its index among the product's options, or -1.
 *
 * Derived, not configured, deliberately: the seller already expressed the axis by uploading photos under
 * one attribute in the variant editor, and a second source of truth (a stored `imageAxis` name) would be
 * one more thing to keep in sync — and to get wrong after a rename.
 */
export function imageAxisIndex(product) {
    const tagged = new Set((product.images ?? [])
        .map((i) => i.optionValue?.trim().toLowerCase())
        .filter((v) => !!v));
    if (tagged.size === 0)
        return -1;
    return (product.options ?? []).findIndex((o) => o.values.some((v) => tagged.has(v.label.trim().toLowerCase())));
}
/**
 * The photos the gallery should show for the CURRENTLY selected variant: that colour's photos first, then
 * the photos that belong to no colour at all (size charts, packaging, lifestyle shots the seller wants on
 * every variant). The colour's own photos lead so the main image IS the chosen colour — the behaviour the
 * founder asked for, and what every large brand does.
 *
 * Falls back to the FULL list, unchanged, whenever the answer would be worse than doing nothing:
 *   • no photo carries a tag (every product before the seller picks an image axis),
 *   • no variant is selected yet,
 *   • the selected value has no photo of its own (an empty gallery is never the right answer).
 */
/**
 * The image-axis VALUE currently selected ("bleu"), normalised — the key a gallery resets its position on
 * when the buyer switches colour. Null when there is no axis or no selection.
 */
export function selectedImageGroup(product, variantLabel) {
    const axis = imageAxisIndex(product);
    if (axis < 0 || !variantLabel)
        return null;
    const parts = variantLabel.split(OPTION_SEP);
    return (parts.length > axis ? parts[axis] : variantLabel)?.trim().toLowerCase() || null;
}
export function imagesForVariant(product, variantLabel) {
    const images = product.images ?? [];
    const axis = imageAxisIndex(product);
    if (axis < 0 || !variantLabel)
        return images;
    // A single-option product's label IS the value; a multi-option label is the " / "-joined tuple.
    const parts = variantLabel.split(OPTION_SEP);
    const selected = (parts.length > axis ? parts[axis] : variantLabel)?.trim().toLowerCase();
    if (!selected)
        return images;
    const mine = images.filter((i) => i.optionValue?.trim().toLowerCase() === selected);
    if (mine.length === 0)
        return images;
    const shared = images.filter((i) => !i.optionValue?.trim());
    return [...mine, ...shared];
}
/**
 * The GALLERY's own list for the selected variant — photos AND videos, in the seller's order (20/09/2026).
 *
 * `imagesForVariant` above stays photos-only and keeps every other caller (the variant pre-warmer, the
 * colourway swatches) pulling `<img>` urls it can safely preload. This one is for the gallery, the single
 * surface that knows how to play a clip.
 *
 * Falls back to the product's photos when there is no `media` list at all — a demo product, a builder
 * canvas, or a storefront answered by a backend that predates product videos. The gallery then behaves
 * exactly as it did before this existed.
 */
export function mediaForVariant(product, variantLabel) {
    const full = product.media && product.media.length > 0
        ? [...product.media]
        : (product.images ?? []).map((i) => ({
            url: i.url,
            alt: i.alt,
            kind: 'image',
            posterUrl: null,
            optionValue: i.optionValue ?? null,
        }));
    // The axis and the selected value are resolved exactly as for the photos — a clip tagged to a colour
    // belongs to that colour's set, and an untagged one belongs to every set.
    const axis = imageAxisIndex(product);
    if (axis < 0 || !variantLabel)
        return full;
    const parts = variantLabel.split(OPTION_SEP);
    const selected = (parts.length > axis ? parts[axis] : variantLabel)?.trim().toLowerCase();
    if (!selected)
        return full;
    const mine = full.filter((i) => i.optionValue?.trim().toLowerCase() === selected);
    if (mine.length === 0)
        return full;
    const shared = full.filter((i) => !i.optionValue?.trim());
    return [...mine, ...shared];
}
//# sourceMappingURL=productImages.js.map